class ProductCard extends HTMLElement {
  selectors = {
    productJSON: '[data-product-json]',
    productId: '[name="id"]',
    productImg: '[data-product-img]',
    productPrice: '[data-product-price]',
    productOneTimePurchasePrice: '[data-product-one-time-purchase-price]',
    productSubscriptionPrice: '[data-product-subscription-price]',
    productBadgeSale: '[data-product-badge-sale]',
    productBadgeDiscount: '[data-product-badge-discount]',
    productDetails: 'input[name="properties[DETAILS]"]',
    arrives: '[data-product-arrives]',
    comingSoon: '[data-product-coming-soon]',
    fastFreeShipping: '[data-product-fast-free-shipping]',
    productShipsFrom: '[data-product-ships-from]',
    productNote: 'input[name="note"]',
    productTitle: '[data-product-title]',
    atcBtn: '[action="/cart/add"] [type="submit"]',
    buyNowBtn: '[action="/cart/add"] [name="buy_now"]',
    productBullets: '[data-product-bullets]',
    productLink: 'a:not([href="#"]):not([href="javascript:void(0)"])',
    imagesContainer: '[data-product-gallery-slider] .swiper-wrapper',
    image: '[data-product-gallery-slider] .swiper-slide',
  };
  #discount = null;
  #currentVariant = null;
  #cachedVariants = null;

  connectedCallback() {
    const productJSONHTML = this.querySelector(this.selectors.productJSON);
    this.product = productJSONHTML ? JSON.parse(productJSONHTML.innerHTML) : null;

    if (!this.product) {
      return;
    }

    this.allImages = this.querySelectorAll(this.selectors.image);
    this.currentVariant =
      this.variants.find(variant => variant.title.includes(Shopify.currentCountry?.voltage)) ||
      this.variants.find(variant => variant.available) ||
      this.variants[0];

    this.setupEventListeners();
  }

  setupEventListeners() {
    const variantSelect = this.querySelector(this.selectors.productId);
    if (variantSelect) {
      variantSelect.addEventListener('change', event => {
        this.currentVariant = this.variants.find(variant => variant.id === +event.target.value);
      });
    }

    Shopify.discount.onUpdated(() => {
      this.#cachedVariants = null;
      this.render();
    });

    const inputSellingPlan = this.querySelector('input[name="selling_plan"]');
    if (inputSellingPlan) {
      const observer = new MutationObserver(mutationsList => {
        mutationsList.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            this.sellingPlanId = +inputSellingPlan.value;
            this.render();
          }
        });
      });

      observer.observe(inputSellingPlan, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }
  }

  get variants() {
    if (this.#cachedVariants) {
      return this.#cachedVariants;
    }

    const discountForOneTimePurchase = Shopify.discount.appliedDiscountCodes.find(code =>
      Shopify.discount.isProductEligibleForDiscount(this.product, code, false),
    );

    const discountForSubscription = Shopify.discount.appliedDiscountCodes.find(code =>
      Shopify.discount.isProductEligibleForDiscount(this.product, code, true),
    );

    this.#cachedVariants = this.product.variants.map(variant => {
      const originalPrice = variant.price;
      const subscriptionOriginalPrice =
        variant.selling_plan_allocations?.find(plan => plan.selling_plan_id === this.sellingPlanId)?.price ??
        variant.selling_plan_allocations?.[0]?.price ??
        originalPrice;

      let newPrice = originalPrice;
      let subscriptionPrice = subscriptionOriginalPrice;

      if (discountForOneTimePurchase) {
        newPrice = Shopify.discount.getPriceWithDiscount(originalPrice, discountForOneTimePurchase);
      }

      if (discountForSubscription) {
        subscriptionPrice = Shopify.discount.getPriceWithDiscount(subscriptionOriginalPrice, discountForSubscription);
      }

      return {
        ...variant,
        price: newPrice,
        subscription_price: subscriptionPrice,
        compare_at_price: Math.max(variant.compare_at_price, originalPrice),
      };
    });

    return this.#cachedVariants;
  }

  get currentVariant() {
    if (!this.#currentVariant) return null;
    return this.variants.find(variant => variant.id === this.#currentVariant.id);
  }

  set currentVariant(value) {
    this.#currentVariant = value;
    this.render();
  }

  get discount() {
    return this.#discount;
  }

  render() {
    if (!this.currentVariant) return;

    this.updateTitle();
    this.updateLinks();
    this.updatePrice();
    this.updateBullets();
    this.updateOptions();
    this.updateImages();
    this.updateAvailable();
    this.updateAttr();
    this.updateProductGiftPage();
    this.updateProductDropPage();
  }

  updateTitle() {
    const title = this.querySelector(this.selectors.productTitle);
    if (!title) return;
    title.innerHTML = this.currentVariant.full_title;
  }

  updateLinks() {
    const links = this.querySelectorAll(this.selectors.productLink);

    if (links.length === 0) return;

    Array.from(links, $link => {
      const href = `${window.location.origin}${$link.getAttribute('href')}`;
      const url = new URL(href);
      const params = new URLSearchParams(url.search);
      params.set('variant', this.currentVariant.id);
      $link.setAttribute('href', `${url.pathname}?${params}`);
    });
  }

  updatePrice() {
    const priceDiv = this.querySelector(this.selectors.productPrice);
    const oneTimePurchasePriceDiv = this.querySelector(this.selectors.productOneTimePurchasePrice);
    const subscriptionPriceDiv = this.querySelector(this.selectors.productSubscriptionPrice);
    const saleDiv = this.querySelector(this.selectors.productBadgeSale);
    const discountDiv = this.querySelector(this.selectors.productBadgeDiscount);

    if (!priceDiv) return;

    const { price, compare_at_price: compareAtPrice, subscription_price: subscriptionPrice } = this.currentVariant;

    priceDiv.innerHTML = `
      ${price < compareAtPrice ? `<span class="compare-at-price">${formatMoney(compareAtPrice)}</span>` : ''}
      <span class="money">${formatMoney(price)}</span>
    `;

    if (oneTimePurchasePriceDiv) {
      oneTimePurchasePriceDiv.innerHTML = `<span class="money">${formatMoney(price)}</span>`;
    }

    if (subscriptionPriceDiv) {
      subscriptionPriceDiv.innerHTML = `<span class="money">${formatMoney(subscriptionPrice)}</span>`;
    }

    const discountPercentage =
      compareAtPrice > price
        ? Math.round(((compareAtPrice - price) * 100) / compareAtPrice)
        : this.getTagDiscount() || 0;

    if (discountDiv) {
      discountDiv.innerHTML = discountPercentage
        ? `<div class="discount-badge"><div>${discountPercentage}% <span>OFF</span></div></div>`
        : '';
    }

    if (saleDiv) {
      saleDiv.innerHTML =
        discountPercentage > 0 ? '<div class="product__badge product__badge--sale">Special Offer</div>' : '';
    }
  }

  getTagDiscount() {
    const saleTags = this.product.tags.filter(tag => tag.includes('sale_'));
    return saleTags.length ? saleTags[0].replace('sale_', '') : null;
  }

  updateBullets() {
    const productBullets = this.querySelector(this.selectors.productBullets);
    if (!productBullets) return;
    productBullets.innerHTML = this.currentVariant.bullets;
  }

  updateOptions() {
    const swatchTypeCard = this.querySelector("[data-swatch-type='card']");

    if (!swatchTypeCard) return;

    const swatchTypeCardIndex = Number(swatchTypeCard.getAttribute('data-option-index'));

    const variants = this.variants.filter(({ options }) =>
      this.currentVariant.options.every((option, index) => index === swatchTypeCardIndex || options.includes(option)),
    );

    variants.forEach(variant => {
      const image = `${variant.featured_image}${variant.featured_image.includes('?') ? '&' : '?'}&width=60`;

      // this.querySelector(`[data-value="${variant.options[swatchTypeCardIndex].replace(/\"/g, '')}"] .money`).innerHTML =
      //   formatMoney(variant.price);

      this.querySelector(
        `[data-value="${variant.options[swatchTypeCardIndex].replace(/\"/g, '')}"] .swatch-variant-img`,
      )?.setAttribute('srcset', image);
    });
  }

  updateImages() {
    const image = this.querySelector(this.selectors.productImg);

    if (image) {
      image.setAttribute('srcset', formatImage(this.currentVariant.featured_image, '400x'));
    } else {
      this.updateImagesList();
    }
  }

  updateAvailable() {
    const atcBtn = this.querySelector(this.selectors.atcBtn);
    const buyNowBtn = this.querySelector(this.selectors.buyNowBtn);
    const isPreOrder = this.currentVariant.inventory_management !== null && this.currentVariant.inventory_policy === 'continue' && this.currentVariant.inventory_quantity < 1;

    if (!atcBtn) return;

    if (this.currentVariant.available) {
      atcBtn.removeAttribute('disabled');

      if (atcBtn.innerText.toLowerCase() === Shopify.strings.soldOut.toLowerCase()) {
        atcBtn.innerHTML = Shopify.strings.addToCart;
      }

      if (buyNowBtn) {
        buyNowBtn.innerHTML = isPreOrder ? Shopify.strings.preOrder : Shopify.strings.buyNow;
      }
    } else {
      atcBtn.setAttribute('disabled', 'disabled');

      if (atcBtn.innerText.toLowerCase() === Shopify.strings.addToCart.toLowerCase()) {
        atcBtn.innerHTML = Shopify.strings.soldOut;
      }

      if (buyNowBtn) {
        buyNowBtn.innerHTML = Shopify.strings.soldOut;
      }
    }
  }

  updateAttr() {
    const detailsInput = this.querySelector(this.selectors.productDetails);
    const noteInput = this.querySelector(this.selectors.productNote);
    const comingSoonDiv = this.querySelector(this.selectors.comingSoon);
    const fastFreeShippingDiv = this.querySelector(this.selectors.fastFreeShipping);
    const productShipsFromDiv = this.querySelector(this.selectors.productShipsFrom);

    const sku = this.currentVariant.sku || this.currentVariant.title;
    let plug = Shopify.currentCountry?.plug;

    const usuallyShips = getUsuallyShips(this.currentVariant);
    const shipsFrom = getShipsFrom(this.currentVariant);
    const isShipsFromChina = shipsFrom ? shipsFrom.toLowerCase().includes('china') : false;
    const isPreOrder = this.currentVariant.inventory_management !== null && this.currentVariant.inventory_policy === 'continue' && this.currentVariant.inventory_quantity < 1;

    if (this.currentVariant.plugs.every(p => !p.includes(plug))) {
      plug = this.currentVariant.plugs[0];
    }

    if (detailsInput) {
      detailsInput.value = `SKU: ${sku} - Plug: ${plug}`;
    }

    if (noteInput) {
      noteInput.value = `SKU: ${sku} - Plug: ${plug}`;
    }

    if (fastFreeShippingDiv) {
      if (isShipsFromChina || isPreOrder) {
        fastFreeShippingDiv.style.display = 'none';
      } else {
        // fastFreeShippingDiv.style.display = 'block';
      }
    }

    if (productShipsFromDiv) {
      if (usuallyShips) {
        productShipsFromDiv.innerHTML = usuallyShips;
        productShipsFromDiv.style.display = 'block';
      } else {
        productShipsFromDiv.style.display = 'none';
      }
    }

    if (!isShipsFromChina && isPreOrder) {
      if (fastFreeShippingDiv) {
        fastFreeShippingDiv.style.display = 'none';
      }

      if (comingSoonDiv) {
        // comingSoonDiv.style.display = 'block';
      }
    } else {
      if (comingSoonDiv) {
        comingSoonDiv.style.display = 'none';
      }
    }
  }

  updateProductGiftPage() {
    const comingSoonDiv = this.querySelector(this.selectors.comingSoon);
    const fastFreeShippingDiv = this.querySelector(this.selectors.fastFreeShipping);
    const productShipsFromDiv = this.querySelector(this.selectors.productShipsFrom);

    if (this.product.gift_card || this.product.tags.includes('GIFT')) {
      if (comingSoonDiv) {
        comingSoonDiv.style.display = 'none';
      }

      if (productShipsFromDiv) {
        productShipsFromDiv.style.display = 'none';
      }

      if (fastFreeShippingDiv) {
        fastFreeShippingDiv.style.display = 'none';
      }
    }
  }

  updateProductDropPage() {
    const fastFreeShippingDiv = this.querySelector(this.selectors.fastFreeShipping);
    const productShipsFromDiv = this.querySelector(this.selectors.productShipsFrom);

    if (this.product.tags.includes('DROP')) {
      const content = '<span>Third-party seller</span><span>Free shipping only for the US</span>';

      if (fastFreeShippingDiv) {
        fastFreeShippingDiv.innerHTML = content;
      }

      if (productShipsFromDiv) {
        productShipsFromDiv.style.display = 'none';
      }
    }
  }

  updateImagesList() {
    const imagesContainer = this.querySelector(this.selectors.imagesContainer);

    if (!imagesContainer || this.allImages.length === 0) return;

    imagesContainer.innerHTML = '';

    const images = Array.from(this.allImages).filter(image => {
      const alt = image.dataset.alt;

      if (alt?.includes(this.currentVariant.handle)) {
        return true;
      }

      const variantExists = this.variants.some(({ handle }) => alt?.includes(handle));
      return !variantExists;
    });

    if (!images.length) {
      images.push(this.allImages[0]);
    }

    images.forEach(image => {
      imagesContainer.append(image);
    });
  }
}

customElements.define('product-card', ProductCard);
